import type { ComputedRef, Ref, WritableComputedRef } from 'vue';
import type { ManipulateType } from 'dayjs';
import type { ReadOnlyTimes, TimePeriodData } from '@/types/common.types';
import type { IconType } from '@/types/icon.types';
import {
  GLOBAL_TIME_LIVE_FORMAT,
  GLOBAL_TIME_RANGE_FORMAT,
  GLOBAL_TIME_MINUTE_FORMAT,
  UTC_DATE_FORMAT,
  UTC_TIME_FORMAT,
  UTC_TIME_FORMAT_MILLISECOND,
} from './constants';

// ============================================================
// Core Types - 핵심 도메인 타입
// ============================================================

/**
 * 시간 범위 정보를 담는 핵심 인터페이스
 * - timePeriod: 기간 문자열 (예: 'p5m', 'p1h', 'pa')
 * - fromTime/toTime: 로컬 타임존 시간
 * - fromTimeUtc/toTimeUtc: UTC 시간
 * - isPaused: 실시간 갱신 일시정지 여부
 */
export interface TimePeriodInfo {
  timePeriod: 'pa' | string;
  fromTime: string;
  toTime: string;
  fromTimeUtc: string;
  toTimeUtc: string;
  isPaused: boolean;
  isCurrentPaused?: boolean;
  noRepeat?: boolean;
}

/**
 * TimeController 옵션
 */
export interface TimePeriodOptions {
  /** 갱신 주기 (예: 'I5s', 'p5s') - useCurrent === true 경우에만 사용 */
  interval?: 'I5s' | string;
  /** 최대 시간 범위 제한 (초) */
  timeRangeSecLimit?: number;
  /** 최소 시간 범위 제한 (초) */
  minTimeRangeSecLimit?: number;
  /** Live 모드 시간 제한 (초) */
  useLiveSecLimit?: number;
  /** 새로고침 버튼 비활성화 여부 */
  disableRefresh?: boolean;
  /** 설정 불가능한 시간 단위 */
  readonlyTimes?: ReadOnlyTimes;
  /** Live Time  시간 포맷 */
  liveFormat?: string;
  /** Range 시간 포맷 */
  rangeFormat?: string;
}

/**
 * 시간 범위 파라미터
 */
export interface TimeRangeParams {
  period: string;
  fromTime: string;
  toTime: string;
}

/**
 * 시간 포맷 타입
 */
export type TimeFormat =
  | typeof GLOBAL_TIME_LIVE_FORMAT
  | typeof GLOBAL_TIME_RANGE_FORMAT
  | typeof GLOBAL_TIME_MINUTE_FORMAT
  | typeof UTC_TIME_FORMAT
  | typeof UTC_TIME_FORMAT_MILLISECOND
  | typeof UTC_DATE_FORMAT;

// ============================================================
// Button Types - 버튼 관련 타입
// ============================================================

export const BUTTON_TYPE = {
  BACK: 'back',
  PAUSE: 'pause',
  FORWARD: 'forward',
  REFRESH: 'refresh',
} as const;

export type ButtonType = (typeof BUTTON_TYPE)[keyof typeof BUTTON_TYPE];

export interface PeriodControlButtonData {
  key: ButtonType;
  icon: IconType;
  disabled?: boolean;
  click: () => void;
}

// ============================================================
// Mode Types - 모드 관련 타입
// ============================================================

export const MODE = {
  CURRENT: 'CURRENT',
  CURRENT_AND_PERIOD: 'CURRENT_AND_PERIOD',
  PERIOD: 'PERIOD',
  CURRENT_PERIOD_CALENDAR: 'CURRENT_PERIOD_CALENDAR',
  PERIOD_CALENDAR: 'PERIOD_CALENDAR',
  // 신규 모드
  CURRENT_AND_PERIOD_QUICK: 'CURRENT_AND_PERIOD_QUICK',
  PERIOD_CALENDAR_INPUT: 'PERIOD_CALENDAR_INPUT',
  CURRENT_PERIOD_CALENDAR_INPUT: 'CURRENT_PERIOD_CALENDAR_INPUT',
  PERIOD_CALENDAR_INPUT_WITHOUT_NAVIGATOR: 'PERIOD_CALENDAR_INPUT_WITHOUT_NAVIGATOR',
} as const;

export type Mode = (typeof MODE)[keyof typeof MODE] | '';

// ============================================================
// Actor Component Interfaces - Actor 컴포넌트 인터페이스
// ============================================================

/**
 * LiveUpdater 컴포넌트 Props
 * 백그라운드에서 시간 tick을 발생시키는 renderless 컴포넌트
 */
export interface LiveUpdaterProps {
  /** 일시정지 여부 */
  isPaused?: boolean;
  /** 갱신 주기 (예: 'p5s', 'p10s') */
  interval?: string;
  /** 비활성화 여부 */
  disabled?: boolean;
}

export interface LiveUpdaterEmits {
  (e: 'tick', serverTime: string): void;
}

/**
 * PeriodSelector 컴포넌트 Props
 * 기간 선택 드롭다운 컴포넌트
 */
export interface PeriodSelectorProps {
  modelValue: TimePeriodInfo;
  items?: TimePeriodData[];
  disabled?: boolean;
}

export interface PeriodSelectorEmits {
  (e: 'update:modelValue', value: TimePeriodInfo): void;
  (e: 'select', period: string): void;
}

/**
 * CalendarSelector 컴포넌트 Props
 * 캘린더 선택 컴포넌트
 */
export interface CalendarSelectorProps {
  modelValue: TimePeriodInfo;
  maxRangeSec?: number;
  minRangeSec?: number;
  disabled?: boolean;
}

export interface CalendarSelectorEmits {
  (e: 'update:modelValue', value: TimePeriodInfo): void;
  (e: 'select', range: { fromTime: string; toTime: string }): void;
}

/**
 * TimePeriodDisplay 컴포넌트 Props
 * 시간 표시 전용 컴포넌트
 */
export interface TimePeriodDisplayProps {
  periodInfo: TimePeriodInfo;
  /** 표시 모드: 'live' = 현재 시간, 'range' = 범위 */
  format?: 'live' | 'range';
  /** Live 모드 시간 포맷 */
  liveFormat?: string;
  /** Range 모드 시간 포맷 */
  rangeFormat?: string;
}

// ============================================================
// Internal Types - 내부 사용 타입
// ============================================================

/**
 * Worker 메시지 타입
 */
export interface TimeUpdateMessage {
  type: 'START' | 'STOP' | 'SET_TIME';
  data?: {
    interval?: number;
    currentTime?: string;
  };
}

export interface TimeResponseMessage {
  type: 'TIME_UPDATE';
  currentTime: Date;
}

/**
 * 시간 단위 정보
 */
export interface TimeUnit {
  labelSingle: string;
  labelPlural: string;
  duration: number;
}

// ============================================================
// Internal Composable Types - 내부 Composable 타입
// ============================================================

/**
 * useCurrent composable 파라미터
 */
export interface UseCurrentParams {
  interval: ComputedRef<{ offset: number; unit: ManipulateType }>;
  timePeriodInfo: WritableComputedRef<TimePeriodInfo>;
}

/**
 * useCurrentAndPeriod composable 파라미터
 */
export interface UseCurrentAndPeriodParams {
  interval: ComputedRef<{ offset: number; unit: ManipulateType }>;
  periodItems: Ref<TimePeriodData[]>;
  selectedPeriod: Ref<string>;
  timePeriodInfo: WritableComputedRef<TimePeriodInfo>;
}

/**
 * usePeriod composable 파라미터
 */
export interface UsePeriodParams {
  periodItems: Ref<TimePeriodData[]>;
  selectedPeriod: Ref<string>;
  timePeriodInfo: WritableComputedRef<TimePeriodInfo>;
}

/**
 * useCalendar composable 파라미터
 */
export interface UseCalendarParams {
  interval: Ref<{ offset: number; unit: ManipulateType }>;
  periodItems: Ref<TimePeriodData[]>;
  selectedPeriod: Ref<string>;
  timePeriodInfo: Ref<TimePeriodInfo>;
  useLive?: boolean;
  useLiveSecLimit?: number;
  maxTimeRangeSecLimit?: ComputedRef<number | undefined>;
}
